// Opções do "Somar por..." derivadas do catálogo: o que dá para agrupar, o que
// dá para calcular e como cada conta aparece na tela. Sem React aqui de
// propósito — é a parte que os testes cobrem sem montar a página.

export const MAX_COLUNAS_AGRUPAMENTO = 3
export const MAX_METRICAS = 4
// Quantas colunas vêm marcadas quando o catálogo não declara nenhuma como padrão.
export const COLUNAS_PADRAO = 6
// Colunas numéricas que dizem QUANDO foi a venda: agrupadas por elas, a ordem é a do tempo.
export const COLUNAS_DE_TEMPO = ['hora', 'dia', 'mes', 'ano', 'diaSemana']
export const LIMITE_MINIMO = 1
export const LIMITE_MAXIMO = 1000
export const ROTULO_CONTAGEM_PADRAO = 'Qtd. de linhas'
export const ERRO_LIMITE = 'Digite um número de 1 a 1000, ou deixe em branco para ver todos.'

// Só dá para somar/tirar média de coluna numérica; texto e data ficam de fora.
// Hora, dia, mês e dia da semana são numéricos mas o catálogo marca agregavel:false
// — são o "quando", não o "quanto": "Soma de Mês" não quer dizer nada.
export const TIPOS_QUE_SOMAM = ['numero', 'dinheiro']
export const FUNCOES = ['soma', 'media', 'contagem']

const PREFIXO_ROTULO = { soma: 'Soma de', media: 'Média de' }
const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

// "Dia da semana (0 = domingo)" vira "Dia da semana": o parêntese explica a coluna
// crua, não serve de título de gráfico nem de "Sem ...".
export function rotuloCurto(label) {
    return String(label || '').replace(/\s*\([^)]*\)\s*$/, '').trim() || String(label || '')
}

// Grupo sem valor: "Sem cliente", "Sem marca" — o padrão que o filtro do Analytics já usa.
export function rotuloSemValor(coluna) {
    return `Sem ${rotuloCurto(coluna?.label || 'valor').toLowerCase()}`
}

export function nomeDiaSemana(valor) {
    // Number(null) e Number('') são 0: sem esta guarda, grupo vazio viraria domingo.
    if (valor === null || valor === undefined || String(valor).trim() === '') {
        return ''
    }

    const indice = Number(valor)
    return Number.isInteger(indice) ? DIAS_SEMANA[indice] || '' : ''
}

// Dataset que já vem somado (mesas e comandas) não oferece "Totais".
export function podeAgrupar(dataset) {
    return !!dataset && dataset.agrupavel !== false
}

// Colunas que já vêm marcadas ao escolher o relatório: as que o catálogo declara
// como padrão (número, data, loja, valor). Sem nenhuma declarada, as 6 primeiras.
export function colunasPadrao(dataset) {
    const todas = colunasDe(dataset)
    const marcadas = todas.filter(coluna => coluna.padrao === true).map(coluna => coluna.nome)
    return marcadas.length ? marcadas : todas.slice(0, COLUNAS_PADRAO).map(coluna => coluna.nome)
}

export function ehColunaDeTempo(coluna) {
    return !!coluna && (coluna.tipo === 'data' || COLUNAS_DE_TEMPO.includes(coluna.nome))
}

// Ordem que vale quando ninguém escolheu uma: por tempo, do mais antigo para o
// mais novo; por qualquer outra coisa, pela primeira conta, do maior para o menor.
// Sem isso o gráfico mostrava "os 30 primeiros" numa ordem que era a do banco.
export function ordemPadrao(agruparPor, metricas, dataset) {
    const primeira = colunasDe(dataset).find(coluna => coluna.nome === (agruparPor || [])[0])
    if (primeira && ehColunaDeTempo(primeira)) {
        return { coluna: primeira.nome, direcao: 'asc' }
    }

    const conta = nomeMetrica((metricas || [])[0])
    return conta ? { coluna: conta, direcao: 'desc' } : null
}

// O que aparece embaixo do "por": exemplos deste relatório, e onde está o que
// não existe aqui ("produto" mora em Produtos vendidos, não em Vendas).
const DICA_POR = {
    vendas: 'Ex.: Loja, Dia, Cliente, Marca. Para somar por produto, troque "O que você quer ver" para Produtos vendidos.',
    produtos: 'Ex.: Produto, Grupo do produto, Loja, Dia.',
    clientes: 'Ex.: Cliente, Loja, Origem.',
    formasPagamento: 'Ex.: Forma de pagamento, Loja.',
    tempos: 'Ex.: Loja, Dia, Dia da semana.',
}
const DICA_POR_PADRAO = 'Ex.: Loja, Dia.'

export function dicaSomarPor(dataset) {
    return DICA_POR[dataset?.id] || DICA_POR_PADRAO
}

// Explica o cinza: chegou no teto, as outras opções apagam.
export function textoDoTeto(quantas, maximo) {
    if ((quantas || 0) >= maximo) {
        return `Até ${maximo} colunas. Desmarque uma para trocar.`
    }
    return `Até ${maximo} colunas.`
}

// A contagem só é a 2ª conta padrão em quem declara como chamá-la.
function temRotuloContagem(dataset) {
    return typeof dataset?.rotuloContagem === 'string' && dataset.rotuloContagem.trim() !== ''
}

function funcaoDe(metrica) {
    if (!metrica || typeof metrica !== 'object') {
        return ''
    }
    return String(metrica.funcao || '').toLowerCase()
}

function colunasDe(dataset) {
    return (dataset?.colunas || []).filter(Boolean)
}

// Como a contagem se chama na tela: "Qtd. de pedidos" em vendas, e assim por diante.
export function rotuloContagem(dataset) {
    const rotulo = typeof dataset?.rotuloContagem === 'string' ? dataset.rotuloContagem.trim() : ''
    return rotulo || ROTULO_CONTAGEM_PADRAO
}

// Colunas que aceitam soma e média.
export function colunasQueSomam(dataset) {
    return colunasDe(dataset).filter(
        coluna => TIPOS_QUE_SOMAM.includes(coluna.tipo) && coluna.agregavel !== false,
    )
}

export function podeSomar(dataset, nome) {
    return colunasQueSomam(dataset).some(coluna => coluna.nome === nome)
}

// O que pode ir no "por": tudo menos as contas. Separar por Quantidade gera uma
// cor por valor de quantidade — aconteceu na tela real, e não quer dizer nada.
export function colunasParaAgrupar(dataset) {
    return colunasDe(dataset).filter(coluna => !podeSomar(dataset, coluna.nome))
}

// Nome com que a conta volta do backend: contagem, soma_valorTotal, media_valorTotal.
export function nomeMetrica(metrica) {
    const funcao = funcaoDe(metrica)
    if (funcao === 'contagem') {
        return 'contagem'
    }
    if (!PREFIXO_ROTULO[funcao] || !metrica.coluna) {
        return ''
    }
    return `${funcao}_${metrica.coluna}`
}

export function rotuloMetrica(metrica, dataset) {
    const funcao = funcaoDe(metrica)
    if (funcao === 'contagem') {
        return rotuloContagem(dataset)
    }
    if (!PREFIXO_ROTULO[funcao] || !metrica.coluna) {
        return ''
    }
    const coluna = colunasDe(dataset).find(item => item.nome === metrica.coluna)
    return `${PREFIXO_ROTULO[funcao]} ${coluna?.label || metrica.coluna}`
}

// Como a conta aparece dentro do campo "Somar": ali "Soma de Valor total" leria
// "Somar Soma de Valor total" — a soma é o próprio nome da coluna.
export function rotuloNoSeletor(opcao, dataset) {
    if (!opcao) {
        return ''
    }
    if (opcao.funcao === 'contagem') {
        return rotuloContagem(dataset)
    }

    const coluna = colunasDe(dataset).find(item => item.nome === opcao.coluna)
    const nome = coluna?.label || opcao.coluna || ''
    return opcao.funcao === 'media' ? `Média de ${nome}` : nome
}

// Tudo que o usuário pode marcar em "Somar", na ordem em que aparece.
export function opcoesDeMetrica(dataset) {
    const opcoes = [{ nome: 'contagem', funcao: 'contagem', label: rotuloContagem(dataset) }]

    colunasQueSomam(dataset).forEach(coluna => {
        opcoes.push({
            nome: `soma_${coluna.nome}`,
            funcao: 'soma',
            coluna: coluna.nome,
            label: `Soma de ${coluna.label || coluna.nome}`,
        })
        opcoes.push({
            nome: `media_${coluna.nome}`,
            funcao: 'media',
            coluna: coluna.nome,
            label: `Média de ${coluna.label || coluna.nome}`,
        })
    })

    return opcoes
}

// O que já vem marcado ao ligar "Totais": dinheiro é o que o dono olha
// primeiro. A 2ª é a contagem em quem sabe contar ("Qtd. de pedidos"); em quem
// não sabe, a soma da 1ª coluna numérica (Produtos = Valor total + Quantidade).
export function metricasPadrao(dataset) {
    const somaveis = colunasQueSomam(dataset)
    const primeira = somaveis.find(coluna => coluna.tipo === 'dinheiro') || somaveis[0]

    const escolhidas = []
    if (primeira) {
        escolhidas.push({ funcao: 'soma', coluna: primeira.nome })
    }

    if (temRotuloContagem(dataset)) {
        escolhidas.push({ funcao: 'contagem' })
    } else {
        const segunda = somaveis.find(
            coluna => coluna.tipo === 'numero' && coluna.nome !== primeira?.nome,
        )
        if (segunda) {
            escolhidas.push({ funcao: 'soma', coluna: segunda.nome })
        }
    }

    return escolhidas.length ? escolhidas : [{ funcao: 'contagem' }]
}

// "Os 10 maiores" só é verdade se a ordem for por uma das contas, do maior para
// o menor. Ordenado por data crescente, "maiores" seria mentira na tela.
export function ordenadoPelaConta(ordenacao, metricas) {
    if (!ordenacao || String(ordenacao.direcao).toLowerCase() !== 'desc') {
        return false
    }

    return (metricas || []).some(metrica => nomeMetrica(metrica) === ordenacao.coluna)
}

export function temMetrica(metricas, nome) {
    return (metricas || []).some(metrica => nomeMetrica(metrica) === nome)
}

// Campo "Mostrar só os primeiros": vazio significa "todos", não erro.
export function validarLimite(entrada) {
    if (entrada === null || entrada === undefined || String(entrada).trim() === '') {
        return { ok: true, limite: null }
    }

    const numero = Number(String(entrada).trim())
    if (!Number.isInteger(numero) || numero < LIMITE_MINIMO || numero > LIMITE_MAXIMO) {
        return { ok: false, limite: null, erro: ERRO_LIMITE }
    }

    return { ok: true, limite: numero }
}

// Rótulo de qualquer coluna que sai no relatório — inclusive as contas, que não
// estão no catálogo. Sem isso o resumo mostraria "soma_valorTotal" na tela.
export function rotuloSaida(nome, dataset) {
    if (!nome) {
        return ''
    }

    const coluna = colunasDe(dataset).find(item => item.nome === nome)
    if (coluna) {
        return coluna.label || nome
    }

    if (nome === 'contagem') {
        return rotuloContagem(dataset)
    }

    const separador = String(nome).indexOf('_')
    if (separador > 0) {
        const funcao = String(nome).slice(0, separador)
        const alvo = String(nome).slice(separador + 1)
        if (PREFIXO_ROTULO[funcao]) {
            return rotuloMetrica({ funcao, coluna: alvo }, dataset)
        }
    }

    return nome
}
